import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Select } from '@nextiva/next-ui';
import { debounce, omit, differenceWith, isEmpty } from 'lodash';

import { http } from 'lib/services/http';
import { cancelableAction } from 'utils/redux-axios-helpers';

import LabeledMultiSelect from '../LabeledMultiSelect';

class DebouncedAsyncSelect extends Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    optionsBuilder: PropTypes.func.isRequired,
    paramsBuilder: PropTypes.func.isRequired,
    excludeOptions: PropTypes.array,
  };

  static defaultProps = {
    excludeOptions: [],
  };

  state = {
    options: [],
    loading: true,
    search: '',
  };

  constructor(props) {
    super(props);
    this.debouncedLoadOptions = debounce(this.loadOptions, 150, {
      leading: true,
    });
  }

  componentDidMount() {
    this.loadOptions();
  }

  initLoadOptions = (search) => {
    if (this.request) this.request.cancel();
    this.setState({ loading: true, search }, () => this.debouncedLoadOptions());
  };

  loadOptions = () => {
    const { baseUrl, paramsBuilder, optionsBuilder } = this.props;
    const { initialOptions, search } = this.state;
    const params = paramsBuilder(search);
    const metaStateDone = { loading: false, search };

    if (isEmpty(search) && !isEmpty(initialOptions)) {
      return this.setState({ options: initialOptions, ...metaStateDone });
    }

    return cancelableAction(this, ({ cancelToken, cancelable, isCanceled }) => {
      cancelable(
        http
          .get(baseUrl, { params, cancelToken })
          .then((result) => {
            const options = optionsBuilder(result);
            const newState = { options, ...metaStateDone };

            if (isEmpty(search) && isEmpty(initialOptions)) {
              newState.initialOptions = options;
            }

            this.setState(newState);
          })
          .catch((error) => {
            if (isCanceled(error)) return;

            this.setState({ options: [], ...metaStateDone });
          }),
      );
    })();
  };

  getOptions = () => {
    return differenceWith(
      this.state.options,
      this.props.excludeOptions,
      (n, m) => n.value === m.value,
    );
  };

  render() {
    const { loading } = this.state;
    const dataTestId = this.props['data-testid'];
    const noResultsText = loading ? 'Loading...' : 'Nothing found';
    const SelectComponent = this.props.label ? LabeledMultiSelect : Select;
    const selectProps = omit(this.props, [
      'baseUrl',
      'paramsBuilder',
      'optionsBuilder',
    ]);

    return (
      <SelectComponent
        {...selectProps}
        options={loading ? [] : this.getOptions()}
        searchable
        onInputChange={(inputValue) => this.initLoadOptions(inputValue)}
        onBlur={() => {}}
        onCloseResetsInput={false}
        onBlurResetsInput={false}
        onSelectResetsInput={false}
        noResultsText={noResultsText}
        data-testid={dataTestId}
      />
    );
  }
}

export default DebouncedAsyncSelect;
